/*
 * Copyright (c) 2023-2026 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

export class PathHelpers {
  // Public
  static LOGIN = '/login'
  static LOGOUT = '/logout'
  static LOGIN_SESSION_EXPIRED = '/login-session-expired'

  static ORGANIZATIONS = '/organizations'
  static PROJECTS = '/projects'
  static EVENTS = '/events'
  static USERS = '/users'
  static ACCOUNT = '/account'
  static IAM_CLIENTS = '/iam-clients'
  static USAGE_BILLING = '/usage-and-billing'

  // Release Management
  static RELEASE_MANAGEMENT = '/release-management'
  static RELEASES = '/release-management/releases'
  static ENVIRONMENTS = '/release-management/environments'
  static ENVIRONMENT_GROUPS = '/release-management/environments?tab=groups'
  static CREATE_ENVIRONMENT = `${PathHelpers.ENVIRONMENTS}/create`
  static CREATE_RELEASE = `${PathHelpers.RELEASES}/create`
  static CREATE_RELEASE_TEMPLATE = `${PathHelpers.RELEASES}/templates/create`
  static CREATE_ENVIRONMENT_GROUP = `${PathHelpers.ENVIRONMENTS}/groups/create`
  static SYSTEMS = '/release-management/systems'
  static CREATE_SYSTEM = `${PathHelpers.SYSTEMS}/create`
  static BUILDS = '/release-management/builds'
  static CREATE_BUILD = `${PathHelpers.BUILDS}/create`
  static CHANGES = '/release-management/changes'
  static CREATE_CHANGE = `${PathHelpers.CHANGES}/create`
  static ENVIRONMENT_REQUESTS = '/release-management/environment-requests'
  static CHANGE_REQUESTS = `${PathHelpers.ENVIRONMENT_REQUESTS}?tab=change-requests`
  static CREATE_CHANGE_REQUEST = `${PathHelpers.ENVIRONMENT_REQUESTS}/change-requests/create`
  static BOOKING_REQUESTS = `${PathHelpers.ENVIRONMENT_REQUESTS}?tab=booking-requests`
  static BOOKINGS = `${PathHelpers.ENVIRONMENT_REQUESTS}?tab=bookings`
  static CREATE_BOOKING_REQUEST = `${PathHelpers.ENVIRONMENT_REQUESTS}/booking-requests/create`
  static CREATE_BOOKING = `${PathHelpers.ENVIRONMENT_REQUESTS}/bookings/create`
  static TICKETS = '/release-management/tickets'
  static SETTINGS = '/release-management/settings'
  static SETTINGS_CUSTOM_FIELDS = `${PathHelpers.SETTINGS}/custom-fields`
  static SETTINGS_CUSTOM_FIELD_GROUPS = `${PathHelpers.SETTINGS}/custom-field-groups`
  static MAINTENANCE_ACTIVITY = '/maintenance-activity'
  static DEPLOYMENT_PLANS = '/release-management/deployment-plans'
  static DEPLOYMENT_PLAN_ACTIVITIES = `${PathHelpers.DEPLOYMENT_PLANS}?tab=activities`
  static CREATE_DEPLOYMENT_PLAN = `${PathHelpers.DEPLOYMENT_PLANS}/create`
  static CREATE_DEPLOYMENT_PLAN_ACTIVITY = `${PathHelpers.DEPLOYMENT_PLANS}/activities/create`
  static getDeploymentPlanPagePath = (id: string) => `${PathHelpers.DEPLOYMENT_PLANS}/${id}`
  static getDeploymentPlanActivityPagePath = (id: string) => `${PathHelpers.DEPLOYMENT_PLANS}/activities/${id}`
  static getSystemPagePath = (id: string) => `${PathHelpers.SYSTEMS}/${id}`
  static getBuildPagePath = (id: string) => `${PathHelpers.BUILDS}/${id}`
  static getChangePagePath = (id: string) => `${PathHelpers.CHANGES}/${id}`
  static getChangeRequestPagePath = (id: string) => `${PathHelpers.ENVIRONMENT_REQUESTS}/change-requests/${id}`
  static getBookingRequestPagePath = (id: string) => `${PathHelpers.ENVIRONMENT_REQUESTS}/booking-requests/${id}`
  static getBookingPagePath = (id: string) => `${PathHelpers.ENVIRONMENT_REQUESTS}/bookings/${id}`
  // `ticketKey` is a Jira key, not an opaque id, so it's encoded against slashes and spaces.
  // `redirectPath` carries the caller's full URL so the detail page can restore that search.
  static getTicketPagePath = (ticketKey: string, redirectPath?: string) => {
    const path = `${PathHelpers.TICKETS}/${encodeURIComponent(ticketKey)}`
    // `addSearchParams` always emits at least a `?`, unwanted on links with no redirect to carry.
    return redirectPath ? `${path}${addSearchParams({ redirectPath })}` : path
  }

  static getReleaseTemplatePagePath = (id: string) => `${PathHelpers.RELEASES}/templates/${id}`
  static getReleasePagePath = (id: string) => `${PathHelpers.RELEASES}/${id}`
  static getReleaseChangesTabPath = (id: string) => `${PathHelpers.getReleasePagePath(id)}?tab=changes`
  static getEnvironmentPagePath = (id: string) => `${PathHelpers.ENVIRONMENTS}/${id}`
  static getEnvironmentGroupPagePath = (id: string) => `${PathHelpers.ENVIRONMENTS}/groups/${id}`
  // `monthYear` (`M-YYYY`) is the caller's responsibility so the calendar opens on the month
  // containing this TEBR's maintenance window, rather than defaulting to the current month.
  static getMaintenanceActivityCalendarPath = (tebrId: string, monthYear?: string) =>
    `${PathHelpers.MAINTENANCE_ACTIVITY}${addSearchParams({ bookingRequest: tebrId, monthYear })}`

  // Platform
  static ROLES = '/platform/roles'
  static FEATURE_FLAGS = '/platform/feature-flags'
  static CONTACT_TAGS = '/platform/contact-tags'
  static CUSTOMER_NOTIFICATIONS = '/platform/customer-notifications'
  static NOTIFICATION_TEMPLATES = `${PathHelpers.CUSTOMER_NOTIFICATIONS}/notification-templates`
  static CUSTOM_NOTIFICATION = `${PathHelpers.CUSTOMER_NOTIFICATIONS}/custom-notification`
  static NOTIFICATION_HISTORY = `${PathHelpers.CUSTOMER_NOTIFICATIONS}/notification-history`
  static NOTIFICATION_HISTORY_WEB = `${PathHelpers.CUSTOMER_NOTIFICATIONS}/notification-history/web`
  static NOTIFICATION_HISTORY_EMAIL = `${PathHelpers.CUSTOMER_NOTIFICATIONS}/notification-history/email`
  static SENDGRID_TEMPLATES = '/platform/sendgrid-templates'
  static WHATS_NEW = '/platform/whats-new'

  static CREATE_ORGANIZATION = `${PathHelpers.ORGANIZATIONS}/create`
  static CREATE_PROJECT = `${PathHelpers.PROJECTS}/create`
  static CREATE_EVENT = `${PathHelpers.EVENTS}/create`
  static CREATE_IAM_CLIENTS = `${PathHelpers.IAM_CLIENTS}/create`
  static CREATE_NOTIFICATION_TEMPLATE = `${PathHelpers.NOTIFICATION_TEMPLATES}/create`
  static CREATE_SENDGRID_TEMPLATE = `${PathHelpers.SENDGRID_TEMPLATES}/create`
  static CREATE_WHATS_NEW_POST = `${PathHelpers.WHATS_NEW}/create`

  static getOrganizationPagePath = (id: string) => `${PathHelpers.ORGANIZATIONS}/${id}`
  static getProjectPagePath = (id: string) => `${PathHelpers.PROJECTS}/${id}`
  static getEventPagePath = (id: string) => `${PathHelpers.EVENTS}/${id}`
  static getRolePagePath = (id: string) => `${PathHelpers.ROLES}/${id}`
  static getUserPagePath = (id: string) => `${PathHelpers.USERS}/${id}`
  static getIAMClientPagePath = (id: string) => `${PathHelpers.IAM_CLIENTS}/${id}`
  static getNotificationTemplatePagePath = (id: string) => `${PathHelpers.NOTIFICATION_TEMPLATES}/${id}`
  static getWhatsNewPostPagePath = (id: string) => `${PathHelpers.WHATS_NEW}/${id}`
  static getSendGridTemplatePagePath = (id: string, subuser?: string) =>
    `${PathHelpers.SENDGRID_TEMPLATES}/${id}${addSearchParams({ subuser })}`

  static getUpdateProjectPath = (id: string, redirectPath?: string) =>
    `${PathHelpers.getProjectPagePath(id)}/edit${addSearchParams({ redirectPath })}`

  static getUpdateEventPagePath = (id: string, redirectPath?: string) =>
    `${PathHelpers.getEventPagePath(id)}/edit${addSearchParams({ redirectPath })}`

  static getUpdateOrganizationPagePath = (id: string, redirectPath?: string) =>
    `${PathHelpers.getOrganizationPagePath(id)}/edit${addSearchParams({ redirectPath })}`

  static getUpdateUserPagePath = (id: string, redirectPath?: string) =>
    `${PathHelpers.getUserPagePath(id)}/edit${addSearchParams({ redirectPath })}`

  static getUpdateIAMClientPagePath = (id: string, redirectPath?: string) =>
    `${PathHelpers.getIAMClientPagePath(id)}/edit${addSearchParams({ redirectPath })}`

  static getPrivateUsagePagePath = (searchParams?: SearchParamsObject) =>
    `${PathHelpers.USAGE_BILLING}/private-cloud${addSearchParams({ ...searchParams })}`
}

type SearchParamsObject = {
  [key: string]: string | undefined
}

function addSearchParams(searchParamsObject: SearchParamsObject) {
  const params = new URLSearchParams()

  Object.keys(searchParamsObject).forEach(searchParam => {
    const paramValue = searchParamsObject[searchParam]
    if (paramValue) {
      params.set(searchParam, paramValue)
    }
  })

  return `?${params}`
}
